import {
	Dtos,
	getAllowedIndemnitySignatureMethods,
	getCompanySigningStates,
	IndemnityAgreementSignatureMethod,
	IndemnityStatus,
	SuretyAccountId,
	SuretyType,
} from "@inrev/common";
import {
	accountIndemnityAgreementsQueryKey,
	deriveCompanyIndemnitorsMissingAddress,
	deriveIndemnitors,
} from "@portal/api/account";
import {
	CreateAccountIndemnityAgreementDto,
	useCreateAccountIndemnityAgreement,
	useGetAccountIndemnityAgreementFileUrl,
} from "@portal/api/account/agent";
import { createAccountIndemnityAgreementDtoSchema } from "@portal/api/account/agent/validation";
import { FormDropdown } from "@portal/components/form/fields";
import { Button, Text } from "@portal/components/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { indemnityAgreementSignatureMethodTypeDisplayMap } from "../../../../constants";
import { typedZodResolver } from "../../../../utils/form";
import { IndemnityFormData } from "../../../account/types";
import { useAccountTabsNavigation } from "../../../account/useAccountTabNavigation";
import { AccountIndemnitorList } from "../status/submitted/underwritten/indemnity/components/AccountIndemnitorList";
import { IndemnityAgreementStatusIndicator } from "../status/submitted/underwritten/indemnity/components/IndemnityAgreementStatusIndicator";
import { IndemnityAgreementSectionLayout } from "../status/submitted/underwritten/indemnity/layout/IndemnityAgreementLayout";
import { CompletedIndemnityAgreementSection } from "../status/submitted/underwritten/indemnity/sections/CompletedIndemnityAgreementSection";
import { ElectronicIndemnityAgreementSection } from "../status/submitted/underwritten/indemnity/sections/ElectronicIndemnityAgreementSection";
import { PhysicalIndemnityAgreementSection } from "../status/submitted/underwritten/indemnity/sections/PhysicalIndemnityAgreementSection";

type IndemnityAgreementSectionProps = {
	accountId: SuretyAccountId;
	companies: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[];
	individuals: Dtos.Individual.Get.Response[];
	indemnityAgreement?: Dtos.SuretyAccount.IndemnityAgreement.Get.Response;
	isAccountClosed: boolean;
};

export const IndemnityAgreementSection = ({
	accountId,
	companies,
	individuals,
	indemnityAgreement,
	isAccountClosed,
}: IndemnityAgreementSectionProps) => {
	const domReadyRef = useRef<HTMLDivElement>(null);
	const [electronicToPhysicalOpen, setElectronicToPhysicalOpen] = useState(false);
	const indemnitors = deriveIndemnitors({ companies, individuals }, indemnityAgreement);

	const formMethods = useForm<IndemnityFormData, unknown, CreateAccountIndemnityAgreementDto>({
		defaultValues: {
			suretyType: SuretyType.contract,
			signatureMethod: null,
			companySigners: indemnitors.companies.map((c) => ({
				contactId: c.id,
				signerEmail: null,
			})),
			individualSigners: indemnitors.individuals.map((i) => ({
				contactId: i.id,
				signerEmail: i.signerEmail ?? null,
			})),
		},
		reValidateMode: "onBlur",
		resolver: typedZodResolver(createAccountIndemnityAgreementDtoSchema),
	});

	const watchedSignatureMethod = formMethods.watch("signatureMethod");
	const isIncomplete = !indemnityAgreement?.signatureMethod;
	const navigateTabs = useAccountTabsNavigation();
	const companiesMissingAddress = useMemo(
		() => deriveCompanyIndemnitorsMissingAddress(companies),
		[companies],
	);
	const blockedOnAddress = isIncomplete && !isAccountClosed && companiesMissingAddress.length > 0;
	const signatureMethod = indemnityAgreement?.signatureMethod ?? watchedSignatureMethod;

	const queryClient = useQueryClient();
	const { createAccountIndemnityAgreement, createAccountIndemnityAgreementIsLoading } =
		useCreateAccountIndemnityAgreement();

	const { downloadIndemnityAgreement } = useGetAccountIndemnityAgreementFileUrl({
		accountId,
		asDownload: true,
	});

	const handleSubmit = async (data: CreateAccountIndemnityAgreementDto) => {
		await createAccountIndemnityAgreement({ accountId, data });
		await queryClient.invalidateQueries({
			queryKey: accountIndemnityAgreementsQueryKey(accountId),
		});
		downloadIndemnityAgreement();
	};

	const signingStates = useMemo(() => getCompanySigningStates(companies), [companies]);
	const signatureMethodOptions = useMemo(() => {
		const allowed =
			indemnityAgreement?.allowedSignatureMethods ??
			getAllowedIndemnitySignatureMethods(signingStates);
		return IndemnityAgreementSignatureMethod.options().filter((opt) => allowed.includes(opt.value));
	}, [indemnityAgreement?.allowedSignatureMethods, signingStates]);

	if (indemnityAgreement?.status === IndemnityStatus.complete) {
		return (
			<CompletedIndemnityAgreementSection
				accountId={accountId}
				indemnityAgreement={indemnityAgreement}
			/>
		);
	}

	const signatureMethodFormComponent = isIncomplete ? (
		<FormDropdown
			compact
			control={formMethods.control}
			name="signatureMethod"
			options={signatureMethodOptions}
			placeholder="Select one"
			disabled={isAccountClosed || blockedOnAddress}
			anchor="bottom start"
		/>
	) : (
		<Text type="body" color="primary">
			{indemnityAgreement?.signatureMethod &&
				indemnityAgreementSignatureMethodTypeDisplayMap[indemnityAgreement.signatureMethod]}
		</Text>
	);

	const indemnitorListComponent = (
		<AccountIndemnitorList
			indemnitors={indemnitors}
			showESignatureDetails={
				indemnityAgreement?.signatureMethod === IndemnityAgreementSignatureMethod.electronic
			}
			missingAddress={
				blockedOnAddress
					? {
							companyIds: new Set(companiesMissingAddress.map((company) => company.id)),
							onAddAddress: () => navigateTabs({ tabId: "details", subsectionId: "companies" }),
						}
					: undefined
			}
		/>
	);

	if (blockedOnAddress) {
		return (
			<IndemnityAgreementSectionLayout
				status={<IndemnityAgreementStatusIndicator status="incomplete" signatureMethod={null} />}
				signatureMethod={signatureMethodFormComponent}
				requiredIndemnitors={indemnitorListComponent}
				domReadyRef={domReadyRef}
			/>
		);
	}

	return match(signatureMethod)
		.with(IndemnityAgreementSignatureMethod.electronic, () => (
			<ElectronicIndemnityAgreementSection
				accountId={accountId}
				indemnityAgreement={indemnityAgreement}
				indemnitors={indemnitors}
				formMethods={formMethods}
				isIncomplete={isIncomplete}
				isCreating={createAccountIndemnityAgreementIsLoading}
				signatureMethodComponent={signatureMethodFormComponent}
				indemnitorListComponent={indemnitorListComponent}
				domReadyRef={domReadyRef}
				onSubmit={handleSubmit}
				onOpenElectronicToPhysical={() => setElectronicToPhysicalOpen(true)}
				electronicToPhysicalOpen={electronicToPhysicalOpen}
				onCloseElectronicToPhysical={() => setElectronicToPhysicalOpen(false)}
			/>
		))
		.with(IndemnityAgreementSignatureMethod.physical, () => (
			<PhysicalIndemnityAgreementSection
				accountId={accountId}
				indemnityAgreement={indemnityAgreement}
				isIncomplete={isIncomplete}
				signatureMethodComponent={signatureMethodFormComponent}
				indemnitorListComponent={indemnitorListComponent}
				domReadyRef={domReadyRef}
				onSubmit={formMethods.handleSubmit(handleSubmit)}
			/>
		))
		.with(null, () => (
			<IndemnityAgreementSectionLayout
				status={<IndemnityAgreementStatusIndicator status="incomplete" signatureMethod={null} />}
				signatureMethod={signatureMethodFormComponent}
				requiredIndemnitors={indemnitorListComponent}
				body={
					!isAccountClosed ? (
						<div className="flex justify-center items-center">
							<Button color="primary" disabled>
								Submit
							</Button>
						</div>
					) : undefined
				}
				domReadyRef={domReadyRef}
			/>
		))
		.exhaustive();
};
